import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import { UserManager } from "oidc-client";

import type { INewMessage } from "../../../../../resources-vue/vue-interfaces/messaging-service/i-message";
import type { ITypeaheadOptions } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import { IGetUsersByNameQuery, IUsersList } from "../../../../../resources-vue/vue-interfaces/messaging-service/i-user";
import {
    ICreateThreadRequest,
    IFPPatient
} from "../../../../../resources-vue/vue-interfaces/messaging-service/i-family-portal";
import { IPatient } from "../../../../../resources-vue/vue-interfaces/i-patient";
import { groupNameTooltip } from "../../../../../resources-vue/vue-tooltips/ChatTooltips";
import { PatientService } from "../../../../../services/patient.service";
import { MessageService } from "../../../../../services/message-service";
import { UserService } from "../../../../../services/user-service";
import { EnumsService } from "../../../../../services/enums.service";
import { FamilyPortalService } from "../../../../../services/family-portal-service";
import { EnumMap } from "../../../../../common/enum-map";
import { enumToMap } from "../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { GroupType, GroupTypeEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../../../enums/enums";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import eventBus from "../../../../../utilities/eventBus";

import TagsInput from "../../../../../resources-vue/vue-custom-elements/TagsInput/TagsInput.vue";
import MessageInputHandler from "../../../../../resources-vue/vue-custom-elements/MessagingService/MessageInputHandler/MessageInputHandler.vue";
import MultiSelect from "../../../../../resources-vue/vue-elements/MultiSelect/MultiSelect.vue";
import BranchDefaultFilter from "../../../../../resources-vue/vue-custom-elements/BranchDefaultFilter/BranchDefaultFilter.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";

export default defineComponent({
    components: {
        TagsInput,
        MessageInputHandler,
        MultiSelect,
        BranchDefaultFilter,
        TypeaheadInput
    },

    props: {
        selectedGroupType: {
            type: Number as PropType<GroupTypeEnum>,
            default: null
        }
    },

    data() {
        return {
            _messageService: null as MessageService,
            _usersService: null as UserService,
            _enumsService: null as EnumsService,
            _familyPortalService: null as FamilyPortalService,
            _permissionManager: null as PermissionManager,
            _userManager: null as UserManager,
            _patientService: null as PatientService,
            users: [] as ITypeaheadOptions[],
            newMessage: {
                userIds: [],
                message: "",
                isImportant: false
            } as INewMessage,
            _activeStatus: null as number,
            groupNameTooltip,
            groupTypeEnum: GroupType,
            enumToMap,
            careTeam: null as GroupTypeEnum,
            patient: null as IFPPatient,
            patientsList: [] as IFPPatient[],
            conversationSubject: "" as string
        };
    },

    async created() {
        this._messageService = Container.instance.get(MessageService);
        this._usersService = Container.instance.get(UserService);
        this._enumsService = Container.instance.get(EnumsService);
        this._userManager = Container.instance.get(UserManager);
        this._patientService = Container.instance.get(PatientService);
        this._familyPortalService = Container.instance.get(FamilyPortalService);
        this._permissionManager = Container.instance.get(PermissionManager);

        let patientStatusEnum = await this._enumsService.getPatientStatusNew();
        let patientStatusEnumMap = new EnumMap(patientStatusEnum);
        this._activeStatus = patientStatusEnumMap.getEnumValue("active") as number;
    },

    watch: {
        users: {
            handler() {
                this.usersChanged();
            },
            deep: true
        }
    },

    computed: {
        disableSend() {
            return !this.conversationSubject || !this.careTeam || !this.patient;
        }
    },

    mounted() {
        const availableGroups = Array.from(this.enumToMap(this.groupTypeEnum).entries()).filter(([, value]) =>
            this.hasGroupPermission(value.value)
        );
        const matchingGroup = availableGroups.find(([, value]) => value.value === this.selectedGroupType);
        this.careTeam = matchingGroup ? matchingGroup[1].value : (availableGroups[0]?.[1].value ?? null);
    },
    methods: {
        async getUsersList(filter: string, limit: number) {
            const query: IGetUsersByNameQuery = {
                page: 1,
                pageLength: 25,
                term: filter.trim(),
                conversationParticipantUsers: this.newMessage.userIds
            };
            const data = await this._messageService.getUserByName(query);
            return this.getDisplayUsers(data.items);
        },

        getDisplayUsers(data: IUsersList[]) {
            if (data) {
                const filteredItems = data.filter((user) => !this.newMessage.userIds.includes(user.id));
                return filteredItems.map((user) => ({
                    name: `${user.lastName ? user.lastName + ", " : ""}${user.firstName}`,
                    value: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    emailId: user.emailId
                }));
            }
            return [];
        },

        usersChanged() {
            if (this.newMessage) {
                this.newMessage.userIds = this.users.map((user) => user.value);
            }
        },

        async sendMessage(msg: any) {
            try {
                const query: ICreateThreadRequest = {
                    body: msg.message,
                    conversationGroupType: this.careTeam,
                    patientId: this.patient.patientId,
                    subject: this.conversationSubject
                };
                const response = await this._familyPortalService.createThread(query);
                response.conversationGroupType = this.careTeam;
                eventBus.emit("createThread", response);
            } catch (error) {
                console.error(error, "error");
            }
        },

        async toggleImportant() {
            if (this.newMessage) {
                this.newMessage.isImportant = !this.newMessage.isImportant;
            }
        },

        updateTags(value: any) {
            this.users = value;
            if (this.users.length < 2) this.updateTitle(null);
        },

        updateTitle(value: string) {
            this.newMessage.title = value?.trim();
        },
        routeBacktoConnect() {
            eventBus.emit("updateNewConversation", false);
            this.$router.push(`/central-messaging/clinicians/conversation`);
        },
        async getPatientList(filter: string) {
            const data = await this._familyPortalService.getFPPatients({
                term: filter,
                page: 1,
                pageLength: 10,
                conversationGroupType: this.careTeam
            });
            this.patientsList = data.items;
            return data.items?.map((item: IFPPatient) => ({
                value: item.patientId,
                name: `${item.firstName} ${item.lastName}`,
                agencyName: item.agencyName,
                application: item.application,
                mrn: item.mrn
            }));
        },
        updatePatient(newValue: any) {
            if (!newValue) return;
            this.patient = newValue;
            this.patient.patientId = newValue.value;
        },
        hasGroupPermission(groupType: GroupTypeEnum) {
            return this._permissionManager.checkPermission(
                this.getPermissionEnum(groupType),
                PermissionActionEnum.canEdit
            );
        },
        getPermissionEnum(groupType: GroupTypeEnum) {
            let permissionEnum: ParentPermissionEnum = null;
            switch (groupType) {
                case GroupTypeEnum.Scheduling:
                    permissionEnum = ParentPermissionEnum.familyPortalScheduling;
                    break;
                case GroupTypeEnum.Billing:
                    permissionEnum = ParentPermissionEnum.familyPortalBilling;
                    break;
                case GroupTypeEnum.Clinical:
                    permissionEnum = ParentPermissionEnum.familyPortalClinical;
                    break;
                default:
                    permissionEnum = ParentPermissionEnum.familyPortalOthers;
                    break;
            }
            return permissionEnum;
        }
    }
});
